import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import api from '../../services/api';
import { aiGuardQueries, remediationQueries, settingsQueries, vendorIssueQueries, vendorQueries } from '../../lib/queries';
import { sans, topBtn, linkBtn } from '../Vendors/VendorDetails';
import { VendorLogo } from '../VendorRequests/VendorRequests';
import { SEVERITY_RANK, SeverityDot, apiError, palette, scoreColor } from './remediationShared';

interface Issue { name: string; category: string | null; severity: string; description: string | null }
interface Recipient { email: string; name: string | null }

interface Props {
  isDark: boolean;
  onClose: () => void;
  onCreated: (requestId: string) => void;
  vendor?: { id: string; name: string; domain?: string | null } | null;
  preselected?: string[];
  // Pre-fill from an approved scan-triage agent suggestion (see pages/Remediation/TriageSuggestions.tsx).
  // triageSuggestionId is threaded through to the create call so the backend marks that suggestion resolved.
  defaultTitle?: string;
  defaultMessage?: string;
  defaultDueDate?: string | null;
  triageSuggestionId?: string;
}

const MAX_RISKS = 100;
const REMINDERS = [{ value: '', label: 'No reminder' }, { value: '1', label: '1 day before' }, { value: '3', label: '3 days before' },
  { value: '7', label: '1 week before' }, { value: '14', label: '2 weeks before' }];
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const isoInDays = (days: number) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** "Request remediation": pick a vendor's open findings, then who to ask, by when, and with what message. Opened from a
 *  vendor's Issues tab (vendor and picked findings given) or from the Remediation page (vendor chosen here first). */
export default function RequestRemediationModal({ isDark, onClose, onCreated, vendor: givenVendor, preselected,
  defaultTitle: triageTitle, defaultMessage, defaultDueDate, triageSuggestionId }: Props) {
  const c = palette(isDark);
  const [vendor, setVendor] = useState(givenVendor ?? null);
  const [step, setStep] = useState<'vendor' | 'risks' | 'details'>(givenVendor ? 'risks' : 'vendor');
  const [vendorSearch, setVendorSearch] = useState('');
  const [picked, setPicked] = useState<Set<string>>(new Set(preselected ?? []));
  const [riskSearch, setRiskSearch] = useState('');
  const [title, setTitle] = useState(triageTitle ?? '');
  const [titleTouched, setTitleTouched] = useState(!!triageTitle);
  const [message, setMessage] = useState(defaultMessage ?? '');
  // Due date and reminder start at the workspace defaults (Settings -> Workflow defaults) until changed here,
  // unless a triage suggestion proposed its own (based on the findings' severity).
  const defaultsQuery = useQuery(settingsQueries.defaults());
  const [dueDate, setDueDate] = useState(() => defaultDueDate || isoInDays(defaultsQuery.data?.values.remediation_due_days ?? 30));
  const [reminder, setReminder] = useState(() => {
    const d = defaultsQuery.data?.values.remediation_reminder_days ?? 3;
    return d ? String(d) : '';
  });
  const timingDefaulted = useRef(!!defaultsQuery.data);
  useEffect(() => {
    if (timingDefaulted.current || !defaultsQuery.data) return;
    timingDefaulted.current = true;
    const v = defaultsQuery.data.values;
    setDueDate(prev => (prev === isoInDays(30) ? isoInDays(v.remediation_due_days) : prev));
    setReminder(prev => (prev === '3' ? (v.remediation_reminder_days ? String(v.remediation_reminder_days) : '') : prev));
  }, [defaultsQuery.data]);
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [recipientsSeeded, setRecipientsSeeded] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [error, setError] = useState('');

  const vendorsQuery = useQuery({ ...vendorQueries.list(), enabled: step === 'vendor' });
  const issuesQuery = useQuery({ ...vendorIssueQueries.issues(vendor?.id ?? ''), enabled: !!vendor });
  const inRemediationQuery = useQuery({ ...remediationQueries.vendorRisks(vendor?.id ?? ''), enabled: !!vendor });
  const contactsQuery = useQuery({ ...vendorQueries.contacts(vendor?.id ?? ''), enabled: !!vendor });

  const issues: Issue[] = useMemo(() => [...((issuesQuery.data?.issues ?? []) as Issue[])]
    .sort((a, b) => (SEVERITY_RANK[a.severity] ?? 9) - (SEVERITY_RANK[b.severity] ?? 9) || a.name.localeCompare(b.name)), [issuesQuery.data]);
  const inRemediation = inRemediationQuery.data ?? {};

  // Findings picked elsewhere that the latest scan no longer flags are dropped quietly.
  useEffect(() => {
    if (!issuesQuery.data) return;
    const names = new Set(issues.map(i => i.name));
    setPicked(prev => new Set(Array.from(prev).filter(n => names.has(n))));
  }, [issuesQuery.data, issues]);

  // The vendor's primary contact is asked by default.
  useEffect(() => {
    if (recipientsSeeded || !contactsQuery.data) return;
    const primary = (contactsQuery.data as any[]).find(x => x.is_primary && x.email) || (contactsQuery.data as any[]).find(x => x.email);
    if (primary) setRecipients([{ email: primary.email.toLowerCase(), name: primary.name || null }]);
    setRecipientsSeeded(true);
  }, [contactsQuery.data, recipientsSeeded]);

  const pickedNames = useMemo(() => Array.from(picked).sort(), [picked]);
  const defaultTitle = vendor ? `${vendor.name}: fix ${picked.size || ''} security risk${picked.size === 1 ? '' : 's'}`.replace('  ', ' ') : '';
  const effectiveTitle = titleTouched ? title : defaultTitle;

  // Debounced so ticking several boxes asks for one projection, not one per click.
  const [previewNames, setPreviewNames] = useState<string[]>(pickedNames);
  useEffect(() => {
    const timer = window.setTimeout(() => setPreviewNames(pickedNames), 300);
    return () => window.clearTimeout(timer);
  }, [pickedNames]);
  const previewQuery = useQuery({
    queryKey: ['remediation', 'preview', vendor?.id, previewNames.join('\n')],
    queryFn: async () => {
      const params = new URLSearchParams({ vendor_id: vendor!.id });
      previewNames.forEach(n => params.append('risks', n));
      return (await api.get(`/v1/remediation/preview?${params.toString()}`)).data as { has_scan: boolean; current_score: number | null; projected_score: number | null };
    },
    enabled: !!vendor && step !== 'vendor',
    staleTime: 60_000,
    placeholderData: previous => previous,
  });

  const createMutation = useMutation({
    mutationFn: async () => (await api.post('/v1/remediation', {
      vendor_id: vendor!.id, title: effectiveTitle.trim(), message: message.trim() || null,
      due_date: dueDate || null, reminder_days: dueDate && reminder ? Number(reminder) : null,
      recipients, risks: pickedNames, triage_suggestion_id: triageSuggestionId || null,
    })).data as { id: string },
    meta: { invalidates: [['remediation'], ['triage-suggestions']] },
    onSuccess: data => onCreated(data.id),
    onError: (err: any) => {
      // No working AI provider: the global "AI provider required" popup already shows (services/api.ts).
      if (err?.response?.status === 412 && err?.response?.data?.detail?.code === 'ai_not_configured') return;
      setError(apiError(err, 'Could not send the request.'));
    },
  });

  // Same as Send questionnaire: show the AI popup straight away instead of sending a request the server refuses.
  const aiStatus = useQuery(aiGuardQueries.status());
  const aiBlocked = aiStatus.data?.configured === false;
  const send = () => {
    if (aiBlocked && aiStatus.data) {
      window.dispatchEvent(new CustomEvent('fluxwatch:ai-blocked', {
        detail: { aiMode: aiStatus.data.ai_mode, message: aiStatus.data.reason || '' },
      }));
      return;
    }
    setError('');
    createMutation.mutate();
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !createMutation.isPending) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, createMutation.isPending]);

  const addRecipient = () => {
    const email = newEmail.trim().toLowerCase();
    if (!EMAIL_RE.test(email)) { setError('Enter a valid email address.'); return; }
    if (recipients.some(r => r.email === email)) { setError('That contact is already on the list.'); return; }
    setRecipients([...recipients, { email, name: newName.trim() || null }]);
    setNewEmail('');
    setNewName('');
    setError('');
  };
  const toggleContact = (email: string, name: string | null) => {
    const lower = email.toLowerCase();
    setRecipients(prev => prev.some(r => r.email === lower) ? prev.filter(r => r.email !== lower) : [...prev, { email: lower, name }]);
  };

  const input: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box', padding: '8px 10px', fontSize: 12.5, fontFamily: sans, color: c.ink,
    background: c.surface, border: `1px solid ${c.line}`, borderRadius: 6, outline: 'none',
  };
  const label: React.CSSProperties = { fontSize: 11.5, fontWeight: 600, color: c.ink, marginBottom: 6, display: 'block' };
  const hint: React.CSSProperties = { fontSize: 11, color: c.muted, marginTop: 4 };

  const q = riskSearch.trim().toLowerCase();
  const visibleIssues = issues.filter(i => !q || i.name.toLowerCase().includes(q) || (i.category || '').toLowerCase().includes(q));
  const allVisiblePicked = visibleIssues.length > 0 && visibleIssues.every(i => picked.has(i.name));
  const preview = previewQuery.data;
  const gain = preview?.projected_score != null && preview?.current_score != null ? preview.projected_score - preview.current_score : null;

  const vendors = ((vendorsQuery.data ?? []) as any[])
    .filter(v => v.status !== 'archived')
    .filter(v => !vendorSearch.trim() || `${v.name} ${v.domain}`.toLowerCase().includes(vendorSearch.trim().toLowerCase()))
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  const canContinue = picked.size > 0 && picked.size <= MAX_RISKS;
  const canSend = canContinue && recipients.length > 0 && effectiveTitle.trim().length > 0 && !createMutation.isPending;

  const header = (
    <div style={{ padding: '16px 20px', borderBottom: `1px solid ${c.line}`, display: 'flex', alignItems: 'center', gap: 12 }}>
      {vendor && <VendorLogo name={vendor.name} domain={vendor.domain || ''} size={32} isDark={isDark} />}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: c.ink }}>Request remediation</div>
        <div style={{ fontSize: 12, color: c.muted, marginTop: 2 }}>
          {step === 'vendor' ? 'Choose the vendor to ask' : step === 'risks' ? `Step 1 of 2 · Pick the risks ${vendor?.name} should fix` : 'Step 2 of 2 · Who to ask, and by when'}
        </div>
      </div>
      <button aria-label="Close" onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: c.muted, lineHeight: 1 }}>×</button>
    </div>
  );

  const scoreStrip = vendor && step !== 'vendor' && (
    <div style={{ display: 'flex', alignItems: 'center', gap: 18, padding: '10px 20px', borderBottom: `1px solid ${c.line}`, background: c.surface2, fontSize: 12, color: c.slate, flexWrap: 'wrap' }}>
      <span>{picked.size} of {issues.length} risk{issues.length === 1 ? '' : 's'} selected</span>
      {preview?.has_scan && (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          Score <strong style={{ color: scoreColor(preview.current_score, isDark) }}>{preview.current_score ?? '—'}</strong>
          <span>→</span>
          <strong style={{ color: scoreColor(preview.projected_score, isDark) }}>{picked.size ? preview.projected_score ?? '—' : '—'}</strong>
          <span>projected once fixed{gain && picked.size ? ` (+${gain})` : ''}</span>
        </span>
      )}
    </div>
  );

  let body: React.ReactNode;
  if (step === 'vendor') {
    body = (
      <div style={{ padding: 20 }}>
        <input autoFocus placeholder="Search vendors…" value={vendorSearch} onChange={e => setVendorSearch(e.target.value)} style={{ ...input, marginBottom: 12 }} />
        <div style={{ border: `1px solid ${c.line}`, borderRadius: 8, maxHeight: 380, overflowY: 'auto' }}>
          {vendorsQuery.isPending ? <div style={{ padding: 20, color: c.muted, fontSize: 12.5 }}>Loading vendors…</div>
            : vendors.length === 0 ? <div style={{ padding: 20, color: c.muted, fontSize: 12.5 }}>No vendors match.</div>
            : vendors.map(v => (
              <button key={v.id} onClick={() => { setVendor({ id: v.id, name: v.name, domain: v.domain }); setPicked(new Set()); setStep('risks'); }}
                style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 12px', background: 'none', border: 'none',
                  borderBottom: `1px solid ${c.rowLine}`, cursor: 'pointer', textAlign: 'left', fontFamily: sans }}
                onMouseEnter={e => { e.currentTarget.style.background = c.surface2; }} onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}>
                <VendorLogo name={v.name} domain={v.domain} size={26} isDark={isDark} />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 13, fontWeight: 600, color: c.ink }}>{v.name}</span>
                  <span style={{ display: 'block', fontSize: 11, color: c.muted }}>{v.domain}</span>
                </span>
                {v.score != null && <span style={{ fontSize: 12, fontWeight: 700, color: scoreColor(v.score, isDark) }}>{v.score}</span>}
              </button>
            ))}
        </div>
      </div>
    );
  } else if (step === 'risks') {
    body = (
      <div style={{ padding: 20 }}>
        {issuesQuery.isPending ? <div style={{ color: c.muted, fontSize: 12.5 }}>Loading the latest findings…</div>
          : !issuesQuery.data?.has_scan ? <div style={{ color: c.muted, fontSize: 12.5 }}>This vendor has no completed scan yet, so there is nothing to remediate.</div>
          : issues.length === 0 ? <div style={{ color: c.muted, fontSize: 12.5 }}>The latest scan found no critical, high or medium risks.</div>
          : (
            <>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
                <input placeholder="Search risks…" value={riskSearch} onChange={e => setRiskSearch(e.target.value)} style={{ ...input, maxWidth: 280 }} />
                <button style={linkBtn(isDark)} onClick={() => setPicked(prev => {
                  const next = new Set(prev);
                  visibleIssues.forEach(i => (allVisiblePicked ? next.delete(i.name) : next.add(i.name)));
                  return next;
                })}>{allVisiblePicked ? 'Clear these' : `Select ${q ? 'matching' : 'all'}`}</button>
              </div>
              <div style={{ border: `1px solid ${c.line}`, borderRadius: 8, maxHeight: 360, overflowY: 'auto' }}>
                {visibleIssues.map(issue => {
                  const other = inRemediation[issue.name];
                  return (
                    <label key={issue.name} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 12px', borderBottom: `1px solid ${c.rowLine}`, cursor: 'pointer',
                      background: picked.has(issue.name) ? c.selected : 'transparent' }}>
                      <input type="checkbox" checked={picked.has(issue.name)} onChange={() => setPicked(prev => {
                        const next = new Set(prev);
                        if (next.has(issue.name)) next.delete(issue.name); else next.add(issue.name);
                        return next;
                      })} />
                      <span style={{ width: 78, flexShrink: 0 }}><SeverityDot severity={issue.severity} isDark={isDark} /></span>
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: c.ink }}>{issue.name}</span>
                        <span style={{ display: 'block', fontSize: 11, color: c.muted }}>
                          {issue.category || 'Other'}{other ? ` · already in "${other.title}"` : ''}
                        </span>
                      </span>
                    </label>
                  );
                })}
                {visibleIssues.length === 0 && <div style={{ padding: 16, color: c.muted, fontSize: 12.5 }}>No risks match.</div>}
              </div>
              {picked.size > MAX_RISKS && <div style={{ ...hint, color: c.danger }}>A request can hold up to {MAX_RISKS} risks.</div>}
            </>
          )}
      </div>
    );
  } else {
    const contacts = ((contactsQuery.data ?? []) as any[]).filter(x => x.email);
    body = (
      <div style={{ padding: 20, display: 'grid', gap: 16 }}>
        <div>
          <span style={label}>Title</span>
          <input style={input} value={effectiveTitle} maxLength={200} onChange={e => { setTitleTouched(true); setTitle(e.target.value); }} />
        </div>
        <div>
          <span style={label}>Vendor contacts to ask</span>
          {contacts.length > 0 && (
            <div style={{ display: 'grid', gap: 4, marginBottom: 8 }}>
              {contacts.map(x => (
                <label key={x.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: c.ink, cursor: 'pointer' }}>
                  <input type="checkbox" checked={recipients.some(r => r.email === x.email.toLowerCase())} onChange={() => toggleContact(x.email, x.name)} />
                  <span style={{ fontWeight: 600 }}>{x.name || x.email}</span>
                  <span style={{ color: c.muted }}>{x.name ? x.email : ''}{x.role ? ` · ${x.role}` : ''}{x.is_primary ? ' · Primary' : ''}</span>
                </label>
              ))}
            </div>
          )}
          {recipients.filter(r => !contacts.some(x => x.email.toLowerCase() === r.email)).map(r => (
            <div key={r.email} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: c.ink, marginBottom: 4 }}>
              <span style={{ fontWeight: 600 }}>{r.name || r.email}</span>
              <span style={{ color: c.muted }}>{r.name ? r.email : ''}</span>
              <button style={{ ...linkBtn(isDark), color: c.muted }} onClick={() => setRecipients(recipients.filter(x => x.email !== r.email))}>Remove</button>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
            <input placeholder="Email" value={newEmail} onChange={e => setNewEmail(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') addRecipient(); }} style={{ ...input, flex: 2 }} />
            <input placeholder="Name (optional)" value={newName} onChange={e => setNewName(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') addRecipient(); }} style={{ ...input, flex: 1.4 }} />
            <button style={topBtn(false, isDark)} onClick={addRecipient} disabled={!newEmail.trim()}>Add</button>
          </div>
          <div style={hint}>Each contact gets an email invitation to the FluxWatch vendor portal, where they can reply and submit the request for your review.</div>
        </div>
        <div>
          <span style={label}>Message</span>
          <textarea value={message} onChange={e => setMessage(e.target.value)} rows={4} maxLength={5000}
            placeholder={`Hi, our latest review of ${vendor?.name} found the security risks listed in this request. Please fix them by the due date and submit the request for review once done.`}
            style={{ ...input, resize: 'vertical', lineHeight: 1.5 }} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <span style={label}>Due date</span>
            <input type="date" value={dueDate} min={isoInDays(0)} onChange={e => setDueDate(e.target.value)} style={input} />
          </div>
          <div>
            <span style={label}>Reminder email</span>
            <select value={reminder} onChange={e => setReminder(e.target.value)} disabled={!dueDate} style={{ ...input, cursor: 'pointer' }}>
              {REMINDERS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
            <div style={hint}>Sent daily from then until the vendor submits.</div>
          </div>
        </div>
      </div>
    );
  }

  const footer = (
    <div style={{ padding: '12px 20px', borderTop: `1px solid ${c.line}`, display: 'flex', alignItems: 'center', gap: 8 }}>
      {error && <span style={{ fontSize: 12, color: c.danger, flex: 1 }}>{error}</span>}
      {!error && <span style={{ flex: 1 }} />}
      {step === 'risks' && !givenVendor && <button style={topBtn(false, isDark)} onClick={() => setStep('vendor')}>Back</button>}
      {step === 'details' && <button style={topBtn(false, isDark)} onClick={() => { setError(''); setStep('risks'); }}>Back</button>}
      {step === 'risks' && (
        <button style={{ ...topBtn(true, isDark), opacity: canContinue ? 1 : 0.5 }} disabled={!canContinue} onClick={() => setStep('details')}>
          Continue{picked.size ? ` with ${picked.size}` : ''}
        </button>
      )}
      {step === 'details' && (
        <button style={{ ...topBtn(true, isDark), opacity: canSend ? 1 : 0.5 }} disabled={!canSend} onClick={send}>
          {createMutation.isPending ? 'Sending…' : 'Send request'}
        </button>
      )}
    </div>
  );

  return createPortal(
    <div onMouseDown={e => { if (e.target === e.currentTarget && !createMutation.isPending) onClose(); }}
      style={{ position: 'fixed', inset: 0, zIndex: 1300, background: 'rgba(15,23,42,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, fontFamily: sans }}>
      <div role="dialog" aria-label="Request remediation" style={{ background: c.surface, border: `1px solid ${c.line}`, borderRadius: 10, width: 680, maxWidth: '100%', maxHeight: '92vh', display: 'flex', flexDirection: 'column' }}>
        {header}
        {scoreStrip}
        <div style={{ flex: 1, overflowY: 'auto' }}>{body}</div>
        {footer}
      </div>
    </div>,
    document.body,
  );
}
